import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import AmgPage from "./AmgPage";
import googlePlayBadge from "./badges/google-play-badge.png";
import appStoreBadge from "./badges/app-store-badge.svg";
import { GAMES, useCampaign, playHref, appStoreHref } from "./storeLinks";

/** Per-game copy for the get-the-app smart pages. Store identities live
 *  in storeLinks.js (null appleId/packageName = not on that store yet:
 *  the badge and phone redirect for it drop out automatically). */
const COPY = {
  poker: {
    title: "Get Airplane Mode Poker",
    blurb:
      "Texas Hold'em between the phones at your table — no internet, no " +
      "accounts, no data collected. Grab it for your phone:",
    // Required by ad-platform social-casino policies when this page is an
    // ad destination: adult-audience disclaimer, kept visible.
    disclaimer:
      "Airplane Mode Poker is intended for an adult audience (18+). It " +
      "does not offer real-money gambling or an opportunity to win real " +
      "money or prizes. Practice or success at social casino gaming does " +
      "not imply future success at real-money gambling.",
  },
  "liars-dice": {
    title: "Get Airplane Mode Liar's Dice",
    blurb:
      "Liar's dice between the phones at your table — no internet, no " +
      "accounts, no data collected. Grab it for your phone:",
  },
  "board-games": {
    title: "Get Airplane Mode Classic Board Games",
    blurb:
      "Chess, checkers and backgammon between the phones at your table — " +
      "no internet, no accounts, no data collected. Grab it for your phone:",
  },
};

/** The get-the-app smart pages. Each app's "Share the app" QR and share
 *  sheet point at its own /airplane-mode-games/get/<game> (composeApp
 *  .../ui/InviteScreen.kt APP_LINK_URL); poker builds already in the wild
 *  use the bare /get, so that route stays poker forever.
 *
 *  Phones normally never render this: server.js 302s them straight to
 *  their store, campaign tag included. The client-side redirect below is
 *  the fallback for dev serves and anything the server misses. */
export default function AirplaneModeGamesGetPage({ game = "poker" }) {
  const g = GAMES[game];
  const copy = COPY[game];
  const campaign = useCampaign();
  const playUrl = g.packageName ? playHref(g.packageName, campaign) : null;
  const appStoreUrl = g.appleId ? appStoreHref(g.appleId, campaign) : null;

  useEffect(() => {
    const ua = navigator.userAgent || "";
    if (/android/i.test(ua) && playUrl) {
      window.location.replace(playUrl);
    } else if (/iphone|ipad|ipod/i.test(ua) && appStoreUrl) {
      window.location.replace(appStoreUrl);
    }
  }, [playUrl, appStoreUrl]);

  return (
    <AmgPage title={copy.title}>
      <p>{copy.blurb}</p>
      <div className="amg-badges">
        {playUrl && (
          <a href={playUrl} aria-label={`Get ${g.name} on Google Play`}>
            <img src={googlePlayBadge} alt="Get it on Google Play" />
          </a>
        )}
        {appStoreUrl && (
          <a
            href={appStoreUrl}
            aria-label={`Download ${g.name} on the App Store`}
          >
            <img src={appStoreBadge} alt="Download on the App Store" />
          </a>
        )}
      </div>
      {!appStoreUrl && (
        <p className="amg-effective">Coming soon to the App Store.</p>
      )}
      {copy.disclaimer && <p className="amg-effective">{copy.disclaimer}</p>}
      <p>
        Install before you fly: the game itself never touches the internet,
        but the stores need it once.
      </p>
      <p>
        Find more Airplane Mode games{" "}
        <Link to="/airplane-mode-games">here</Link>.
      </p>
    </AmgPage>
  );
}
